<script lang="ts">
    import { onMount } from "svelte";
    import { AppStore } from "$/stores/AppStore";
    import { tooltip } from "$/utils/tooltip";
    import type { Snippet } from "$/types";
    import { v4 as uuid } from "uuid";

    export let keepTitleOpen = false;
    export let editRole = 3;
    export let card: Snippet;
    export let update: (cardUpdate: {id:uuid, name:string}) => void = () => {};

    let inputRef: HTMLDivElement;

    let showTitleClicked = false;
    export function showTitle() {

        // show input
        showTitleClicked = true;

        // wait for input ref to render
        requestAnimationFrame(() => {
            inputRef?.focus();
        });
    }

    let hasNameSuggestion = card?.nameSuggestion?.trim() ? true : false;
    $: showNameSuggestionButton = hasNameSuggestion && !card.name;
    $: hasName = card?.name && card?.name.length > 0;

    /**
     * This line is here to remove {card.name} from the content HTML because
     * due to Svelte's bug the title was duplicating after re-render.
     */
    $: if(inputRef) inputRef.innerText = card?.name || "";

    function updateOnEnterPress(event: KeyboardEvent): void {
        if (event.key === "Enter") {
            event.preventDefault();
            updateChanges();
        }
    }

    function showHideSuggestion(): void {
        showNameSuggestionButton = inputRef?.innerText?.trim()
            ? false
            : hasNameSuggestion;
    }

    function showHideTitle(): void {
        hasName = inputRef?.innerText?.trim() ? true : false;
    }

    function updateChanges(): void {
        card.name = inputRef?.innerText?.trim();

        const cardUpdate = {
            id: card.id,
            name: card.name,
        };

        update(cardUpdate);
    }

    async function handleBlur(event: Event): Promise<void> {
        updateChanges();
        showHideTitle();
        showTitleClicked = false;
    }

    function handlePaste(event: ClipboardEvent): void {
        const text = event.clipboardData.getData("text/plain");
        document.execCommand("insertHTML", false, text);
    }

    // HACK: This is called onmousedown so it happens before the blur event otherwise
    //       handleBlur will remove the suggestion before it can be added
    async function addNameSuggestion(): Promise<void> {
        const currentName = inputRef.innerText?.trim();

        if (!currentName && card.nameSuggestion) {
            const cardName = card.nameSuggestion.trim();
            inputRef.innerText = cardName;
            card.name = cardName;

            updateChanges();
            showHideSuggestion();
            showHideTitle();
        }
    }

    let initialized = false;
    onMount(() => {
        initialized = true;
    });
</script>

{#if hasName || keepTitleOpen || showTitleClicked}
    <div class="card-content-title-wrapper">
        <div
            class="card-content-title"
            contenteditable={editRole >= 3 &&
                !$AppStore.snippetIsDragging &&
                initialized}
            placeholder="Untitled"
            bind:this={inputRef}
            on:blur={handleBlur}
            on:keypress={updateOnEnterPress}
            on:input|stopPropagation={showHideSuggestion}
            on:paste|preventDefault={handlePaste}
            on:keydown|stopPropagation
            on:keyup|stopPropagation
            on:drop|preventDefault
        ></div>
        {#if showNameSuggestionButton}
            <button
                use:tooltip={"Suggest Title"}
                on:mousedown={addNameSuggestion}
                class="card-content-title__ai-icon"
            >
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke-width="1.5"
                    stroke="currentColor"
                    class="size-6"
                >
                    <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        d="M12 18v-5.25m0 0a6.01 6.01 0 0 0 1.5-.189m-1.5.189a6.01 6.01 0 0 1-1.5-.189m3.75 7.478a12.06 12.06 0 0 1-4.5 0m3.75 2.383a14.406 14.406 0 0 1-3 0M14.25 18v-.192c0-.983.658-1.823 1.508-2.316a7.5 7.5 0 1 0-7.517 0c.85.493 1.509 1.333 1.509 2.316V18"
                    />
                </svg>
            </button>
        {/if}
    </div>
{/if}

<style>
    .card-content-title-wrapper {
        @apply flex items-center justify-between px-4 pt-2 !important;
    }
    .card-content-title {
        @apply reset relative w-full flex-shrink cursor-text resize-none overflow-auto border-0 bg-transparent text-base font-bold leading-snug outline-none !important;
        writing-mode: horizontal-tb !important;
    }
    .card-content-title[contenteditable="true"]::before {
        @apply flex !important;
    }
    .card-content-title:focus {
        @apply outline-none !important;
    }
    .card-content-title:empty:before {
        content: attr(placeholder);
        @apply text-gray-400;
    }
    .card-content-title__ai-icon {
        @apply reset -m-1 flex h-6 w-6 cursor-pointer items-center justify-center self-baseline rounded px-1 text-gray-500 text-inherit hover:bg-gray-200 hover:text-gray-600 !important;
    }
</style>
